import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, TrendingUp, TrendingDown, Search, RefreshCw,
  BarChart3, Calendar, Euro, Filter, Tag, ChevronRight, Award,
  Clock, Layers, ArrowUpDown, Download, Sparkles, Target, Percent
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductCensusPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Products list
  const [products, setProducts] = useState([]);
  const [productFilters, setProductFilters] = useState({
    category: 'all',
    period: 'all',
    minPrice: '',
    maxPrice: '',
    minSales: '',
    sortBy: 'sales_count',
    sortOrder: 'desc',
    search: ''
  });
  const [productsPagination, setProductsPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  
  // Reports
  const [reports, setReports] = useState([]);
  const [reportPeriodType, setReportPeriodType] = useState('month');
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  
  // Smart insights
  const [insights, setInsights] = useState(null);
  
  // Categories
  const [categories, setCategories] = useState([]);
  
  // Modal catégorie
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newCategory, setNewCategory] = useState('');

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  });

  // Charger les catégories
  const loadCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/product-census/categories`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  // Charger le dashboard
  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/product-census/dashboard`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      toast.error('Erreur chargement du dashboard');
    }
    setLoading(false);
  };

  // Charger les produits
  const loadProducts = async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '30',
        sort_by: productFilters.sortBy,
        sort_order: productFilters.sortOrder
      });
      
      if (productFilters.category && productFilters.category !== 'all') {
        params.append('category', productFilters.category);
      }
      if (productFilters.period && productFilters.period !== 'all') {
        params.append('period', productFilters.period);
      }
      if (productFilters.minPrice) {
        params.append('min_price', productFilters.minPrice);
      }
      if (productFilters.maxPrice) {
        params.append('max_price', productFilters.maxPrice);
      }
      if (productFilters.minSales) {
        params.append('min_sales', productFilters.minSales);
      }
      if (productFilters.search) {
        params.append('search', productFilters.search);
      }
      
      const res = await fetch(`${API_URL}/api/product-census/products?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setProductsPagination({
          page: data.page,
          total: data.total,
          totalPages: data.total_pages
        });
      }
    } catch (error) {
      toast.error('Erreur chargement des produits');
    }
  };

  // Charger les rapports
  const loadReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/product-census/reports/by-period?period_type=${reportPeriodType}&year=${reportYear}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      toast.error('Erreur chargement des rapports');
    }
  };

  // Charger les insights
  const loadInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/product-census/smart-insights`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setInsights(data);
      }
    } catch (error) {
      toast.error('Erreur chargement des insights');
    }
  };

  // Mettre à jour la catégorie d'un produit
  const updateProductCategory = async () => {
    if (!selectedProduct || !newCategory) return;
    
    try {
      const res = await fetch(`${API_URL}/api/product-census/products/${encodeURIComponent(selectedProduct.name)}/category?new_category=${newCategory}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Catégorie mise à jour');
        setShowCategoryModal(false);
        loadDashboard();
        loadProducts(productsPagination.page);
      }
    } catch (error) {
      toast.error('Erreur mise à jour');
    }
  };

  // Chargement initial
  useEffect(() => {
    loadCategories();
    loadDashboard();
  }, []);

  // Recharger les produits quand les filtres changent
  useEffect(() => {
    if (activeTab === 'products') {
      loadProducts(1);
    }
  }, [activeTab, productFilters]);

  // Recharger les rapports quand la période change
  useEffect(() => {
    if (activeTab === 'reports') {
      loadReports();
    }
  }, [activeTab, reportPeriodType, reportYear]);

  // Charger les insights
  useEffect(() => {
    if (activeTab === 'insights') {
      loadInsights();
    }
  }, [activeTab]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getCategoryBadge = (category) => {
    const colors = {
      ecran: 'bg-blue-100 text-blue-700',
      batterie: 'bg-green-100 text-green-700',
      coque: 'bg-purple-100 text-purple-700',
      chargeur: 'bg-orange-100 text-orange-700',
      accessoire: 'bg-pink-100 text-pink-700',
      telephone: 'bg-indigo-100 text-indigo-700',
      tablette: 'bg-cyan-100 text-cyan-700',
      reparation: 'bg-amber-100 text-amber-700',
      autre: 'bg-slate-100 text-slate-700'
    };
    
    const cat = categories.find(c => c.id === category);
    return (
      <Badge className={colors[category] || colors.autre}>
        {cat?.label || category}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Package className="h-7 w-7" />
                  Recensement des Produits
                </h1>
                <p className="text-white/80 text-sm">Analyse des ventes et tendances</p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                loadDashboard();
                if (activeTab === 'products') loadProducts(1);
                if (activeTab === 'reports') loadReports();
                if (activeTab === 'insights') loadInsights();
              }}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="census-tab-dashboard">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-2" data-testid="census-tab-products">
              <Package className="h-4 w-4" />
              Produits
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2" data-testid="census-tab-reports">
              <Calendar className="h-4 w-4" />
              Rapports
            </TabsTrigger>
            <TabsTrigger value="insights" className="gap-2" data-testid="census-tab-insights">
              <Sparkles className="h-4 w-4" />
              Insights IA
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-indigo-500" />
                <p className="mt-2 text-muted-foreground">Chargement...</p>
              </div>
            ) : dashboard && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-indigo-700">Produits uniques</p>
                          <p className="text-3xl font-bold text-indigo-800">
                            {dashboard.summary?.total_products || 0}
                          </p>
                        </div>
                        <Package className="h-10 w-10 text-indigo-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700">Total ventes</p>
                          <p className="text-3xl font-bold text-green-800">
                            {dashboard.summary?.total_sales_count || 0}
                          </p>
                        </div>
                        <TrendingUp className="h-10 w-10 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-700">Chiffre d'affaires</p>
                          <p className="text-2xl font-bold text-amber-800">
                            {formatCurrency(dashboard.summary?.total_revenue || 0)}
                          </p>
                        </div>
                        <Euro className="h-10 w-10 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-violet-700">Marge totale</p>
                          <p className="text-2xl font-bold text-violet-800">
                            {formatCurrency(dashboard.summary?.total_margin || 0)}
                          </p>
                        </div>
                        <Percent className="h-10 w-10 text-violet-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Sellers & Least Sellers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Sellers */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-green-700">
                        <TrendingUp className="h-5 w-5" />
                        Top 10 - Plus Vendus
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {dashboard.top_sellers?.map((product, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Badge className="bg-green-600 text-white w-7 h-7 flex items-center justify-center rounded-full">
                                {idx + 1}
                              </Badge>
                              <div>
                                <p className="font-medium text-sm line-clamp-1">{product.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {getCategoryBadge(product.category)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-700">{product.sales_count} ventes</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(product.total_revenue)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Least Sellers */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-red-700">
                        <TrendingDown className="h-5 w-5" />
                        Top 10 - Moins Vendus
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {dashboard.least_sellers?.map((product, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="w-7 h-7 flex items-center justify-center rounded-full">
                                {idx + 1}
                              </Badge>
                              <div>
                                <p className="font-medium text-sm line-clamp-1">{product.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {getCategoryBadge(product.category)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-700">{product.sales_count} vente(s)</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(product.total_revenue)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Category Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-indigo-500" />
                      Répartition par Catégorie
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {dashboard.category_stats?.map((cat, idx) => (
                        <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                          <p className="font-medium">{cat.label}</p>
                          <p className="text-2xl font-bold">{cat.count}</p>
                          <p className="text-sm text-muted-foreground">
                            {cat.quantity} unités • {formatCurrency(cat.revenue)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="col-span-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher un produit..."
                        value={productFilters.search}
                        onChange={(e) => setProductFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  
                  <Select
                    value={productFilters.category}
                    onValueChange={(v) => setProductFilters(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes catégories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={productFilters.period}
                    onValueChange={(v) => setProductFilters(prev => ({ ...prev, period: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Période" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes périodes</SelectItem>
                      <SelectItem value="week">7 derniers jours</SelectItem>
                      <SelectItem value="month">30 derniers jours</SelectItem>
                      <SelectItem value="3months">3 derniers mois</SelectItem>
                      <SelectItem value="semester">6 derniers mois</SelectItem>
                      <SelectItem value="year">Cette année</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={productFilters.sortBy}
                    onValueChange={(v) => setProductFilters(prev => ({ ...prev, sortBy: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Trier par" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales_count">Nb ventes</SelectItem>
                      <SelectItem value="revenue">CA</SelectItem>
                      <SelectItem value="price">Prix moyen</SelectItem>
                      <SelectItem value="name">Nom</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button
                    variant="outline"
                    onClick={() => setProductFilters(prev => ({ 
                      ...prev, 
                      sortOrder: prev.sortOrder === 'desc' ? 'asc' : 'desc' 
                    }))}
                    className="gap-2"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    {productFilters.sortOrder === 'desc' ? 'Décr.' : 'Croiss.'}
                  </Button>
                </div>
                
                {/* Prix min/max */}
                <div className="flex gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Prix min:</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={productFilters.minPrice}
                      onChange={(e) => setProductFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                      className="w-24"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Prix max:</Label>
                    <Input
                      type="number"
                      placeholder="999"
                      value={productFilters.maxPrice}
                      onChange={(e) => setProductFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                      className="w-24"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Min ventes:</Label>
                    <Input
                      type="number"
                      placeholder="1"
                      value={productFilters.minSales}
                      onChange={(e) => setProductFilters(prev => ({ ...prev, minSales: e.target.value }))}
                      className="w-20"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium">#</th>
                        <th className="text-left p-4 font-medium">Produit</th>
                        <th className="text-center p-4 font-medium">Catégorie</th>
                        <th className="text-center p-4 font-medium">Ventes</th>
                        <th className="text-center p-4 font-medium">Quantité</th>
                        <th className="text-right p-4 font-medium">Prix moy.</th>
                        <th className="text-right p-4 font-medium">CA Total</th>
                        <th className="text-right p-4 font-medium">Marge</th>
                        <th className="text-center p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {products.map((product, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-4 text-muted-foreground">
                            {(productsPagination.page - 1) * 30 + idx + 1}
                          </td>
                          <td className="p-4">
                            <p className="font-medium">{product.name}</p>
                            {product.variants_count > 1 && (
                              <p className="text-xs text-muted-foreground">
                                {product.variants_count} variantes
                              </p>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {getCategoryBadge(product.category)}
                          </td>
                          <td className="p-4 text-center">
                            <Badge className="bg-indigo-100 text-indigo-700">
                              {product.sales_count}
                            </Badge>
                          </td>
                          <td className="p-4 text-center">{product.total_quantity}</td>
                          <td className="p-4 text-right">{formatCurrency(product.avg_price)}</td>
                          <td className="p-4 text-right font-medium text-green-700">
                            {formatCurrency(product.total_revenue)}
                          </td>
                          <td className="p-4 text-right font-medium text-violet-700">
                            {formatCurrency(product.total_margin)}
                          </td>
                          <td className="p-4 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedProduct(product);
                                setNewCategory(product.category);
                                setShowCategoryModal(true);
                              }}
                            >
                              <Tag className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    {productsPagination.total} produit(s) trouvé(s)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={productsPagination.page <= 1}
                      onClick={() => loadProducts(productsPagination.page - 1)}
                    >
                      Précédent
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {productsPagination.page} / {productsPagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={productsPagination.page >= productsPagination.totalPages}
                      onClick={() => loadProducts(productsPagination.page + 1)}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Récapitulatif par Période
                  </CardTitle>
                  <div className="flex gap-2">
                    <Select value={reportPeriodType} onValueChange={setReportPeriodType}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Par jour</SelectItem>
                        <SelectItem value="week">Par semaine</SelectItem>
                        <SelectItem value="month">Par mois</SelectItem>
                        <SelectItem value="quarter">Par trimestre</SelectItem>
                        <SelectItem value="semester">Par semestre</SelectItem>
                        <SelectItem value="year">Par année</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={String(reportYear)} onValueChange={(v) => setReportYear(parseInt(v))}>
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium">Période</th>
                        <th className="text-center p-4 font-medium">Nb ventes</th>
                        <th className="text-center p-4 font-medium">Quantité</th>
                        <th className="text-center p-4 font-medium">Produits uniques</th>
                        <th className="text-right p-4 font-medium">Chiffre d'affaires</th>
                        <th className="text-right p-4 font-medium">Marge</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {reports.map((report, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-4 font-medium">{report.period}</td>
                          <td className="p-4 text-center">
                            <Badge variant="outline">{report.sales_count}</Badge>
                          </td>
                          <td className="p-4 text-center">{report.quantity}</td>
                          <td className="p-4 text-center">{report.unique_products}</td>
                          <td className="p-4 text-right font-medium text-green-700">
                            {formatCurrency(report.revenue)}
                          </td>
                          <td className="p-4 text-right font-medium text-violet-700">
                            {formatCurrency(report.margin)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-6">
            {insights && (
              <>
                {/* By Time Period */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['last_month', 'last_3_months', 'last_year'].map((period) => (
                    <Card key={period}>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Clock className="h-5 w-5 text-indigo-500" />
                          {insights[period]?.label}
                        </CardTitle>
                        <CardDescription>
                          {insights[period]?.total_products} produits vendus
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {insights[period]?.top_products?.slice(0, 5).map((product, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-indigo-500 text-white w-6 h-6 flex items-center justify-center rounded-full text-xs">
                                  {idx + 1}
                                </Badge>
                                <span className="text-sm line-clamp-1">{product.display_name}</span>
                              </div>
                              <Badge variant="outline">{product.count}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* By Price Range */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-green-500" />
                      Par Tranche de Prix - Plus Vendus
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      {insights.by_price_range?.map((range) => (
                        <div key={range.id} className="p-4 bg-slate-50 rounded-lg">
                          <p className="font-medium text-center mb-2">{range.label}</p>
                          <p className="text-2xl font-bold text-center text-green-700 mb-3">
                            {range.total_sales} ventes
                          </p>
                          <div className="space-y-1">
                            {range.top_products?.slice(0, 3).map((p, idx) => (
                              <div key={idx} className="text-xs bg-white p-1 rounded flex justify-between">
                                <span className="line-clamp-1">{p.display_name}</span>
                                <Badge variant="outline" className="text-xs ml-1">{p.count}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Catégorie */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Modifier la Catégorie
            </DialogTitle>
            <DialogDescription>
              {selectedProduct?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nouvelle catégorie</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
              Annuler
            </Button>
            <Button onClick={updateProductCategory}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
